import React, { useMemo, useState } from "react";
import { NavLink } from 'react-router-dom';
import { resetPassword } from '../features/auth/restore-password/api/restorePasswordApi';
import { getCurrentLanguageKey, getLanguageSection } from '../shared/services/languageService';

function validateForm(password, language) {
  const { newpassword, repeatpassword } = password;

  if (!newpassword.trim()) {
    return language.requiredNewPassword;
  }

  if (newpassword.length < 8) {
    return language.passwordMinLength;
  }

  if (!repeatpassword.trim()) {
    return language.requiredRepeatPassword;
  }

  if (newpassword !== repeatpassword) {
    return language.passwordMismatch;
  }

  return '';
}

function RestorePass(props) {
  const [password, setPassword] = useState({ newpassword: '', repeatpassword: '' });
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const language = useMemo(() => getLanguageSection('restorepassword'), []);
  const currentLanguage = getCurrentLanguageKey();

  const locationSearch = props.location.search;
  const { uid, urlcode } = useMemo(() => {
    const params = new URLSearchParams(locationSearch);
    return {
      uid: params.get('uid') || '',
      urlcode: params.get('urlcode') || ''
    };
  }, [locationSearch]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setPassword((currentPassword) => ({
      ...currentPassword,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async () => {
    const validationError = validateForm(password, language);

    if (validationError) {
      setError(validationError);
      setMsg('');
      return;
    }

    setError('');
    setMsg('');
    setIsSubmitting(true);

    try {
      await resetPassword({
        idUser: parseInt(uid, 10),
        newPassword: password.newpassword,
        urlCode: urlcode
      });
      setMsg('success');
      setPassword({ newpassword: '', repeatpassword: '' });
      setError('');
    } catch (requestError) {
      setError(language.message_error_pass_1);
      setMsg('');
    } finally {
      setIsSubmitting(false);
    }
  };

  function renderInvalidState() {
    return (
      <div className="mx-auto grid w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-marival lg:grid-cols-[minmax(0,1fr)_430px]">
        <section className="border-b border-slate-200 bg-slate-50 p-8 md:border-b-0 md:border-r md:p-10">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
            <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 9v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M12 17h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              <path d="M10.3 4.8 2.9 18a1.5 1.5 0 0 0 1.3 2.2h15.6a1.5 1.5 0 0 0 1.3-2.2L13.7 4.8a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="mb-4 inline-flex items-center text-xs font-black uppercase text-slate-500">
              {language.eyebrowLabel}
          </div>
          <h1 className="m-0 text-3xl font-black leading-tight text-[#11183f] md:text-4xl">{language.invalidLinkTitle}</h1>
          <p className="mb-0 mt-4 max-w-md text-base leading-7 text-slate-600">{language.invalidLinkText}</p>
        </section>

        <section className="flex flex-col justify-center p-6 md:p-8">
          <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-4">
            <h2 className="m-0 mb-2 text-base font-black text-[#11183f]">{language.errorpage}</h2>
            <p className="m-0 text-sm leading-6 text-slate-600">{language.invalidLinkHelp}</p>
          </div>
          <p className="mb-6 text-sm leading-7 text-slate-600">{language.errortext1}</p>
          <a href="/" className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#f6c833] text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.26)] hover:bg-[#ffd84d]">
            {language.errorbtn}
          </a>
        </section>
      </div>
    );
  }

  function renderSuccessState() {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-marival md:p-10">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600">
          <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 className="m-0 text-3xl font-black leading-tight text-[#11183f]">{language.successTitle}</h1>
        <p className="mx-auto mb-0 mt-4 max-w-xl text-base leading-7 text-slate-600">{language.successText}</p>
        <p className="mx-auto mb-7 mt-3 max-w-xl text-sm leading-6 text-slate-500">{language.successHelp}</p>
        <NavLink
          to={`/login${currentLanguage ? `?lang=${currentLanguage}` : ''}`}
          className="inline-flex h-12 w-full max-w-sm items-center justify-center rounded-xl bg-[#f6c833] text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.26)] hover:bg-[#ffd84d]"
        >
          {language.loginbtn}
        </NavLink>
      </div>
    );
  }

  function renderFormState() {
    const { newpassword, repeatpassword } = password;

    return (
      <div className="mx-auto grid w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-marival lg:grid-cols-[minmax(0,1fr)_430px]">
        <section className="border-b border-slate-200 bg-slate-50 p-8 md:border-b-0 md:border-r md:p-10">
          <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
            <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M5 11h14v9H5v-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="mb-4 text-xs font-black uppercase text-slate-500">
              {language.eyebrowLabel}
          </div>
          <h1 className="m-0 text-3xl font-black leading-tight text-[#11183f] md:text-4xl">{language.restore}</h1>
          <p className="mb-0 mt-4 max-w-md text-base leading-7 text-slate-600">{language.text2}</p>
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="m-0 mb-2 text-base font-black text-[#11183f]">{language.panelTitle}</h2>
            <p className="m-0 text-sm leading-6 text-slate-600">{language.panelText}</p>
          </div>
        </section>

        <section className="p-6 md:p-8">
          <p className="m-0 mb-6 text-sm font-black text-[#f5660b]">{language.text1}</p>

          {error ? (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-700">
              {error}
            </div>
          ) : null}

          <div className="mb-5">
            <label className="mb-2 block text-sm font-black text-[#11183f]">{language.newpass}</label>
            <input
              type="password"
              className="mr-login-input h-12 w-full rounded-[10px] border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#11183f] shadow-none"
              name="newpassword"
              value={newpassword}
              onChange={handleInputChange}
              placeholder={language.newpassPlaceholder}
            />
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm font-black text-[#11183f]">{language.renewpass}</label>
            <input
              type="password"
              className="mr-login-input h-12 w-full rounded-[10px] border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#11183f] shadow-none"
              name="repeatpassword"
              value={repeatpassword}
              onChange={handleInputChange}
              placeholder={language.renewpassPlaceholder}
            />
          </div>

          <button
            type="button"
            className="inline-flex h-12 w-full items-center justify-center rounded-xl border-0 bg-[#f6c833] text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.26)] hover:bg-[#ffd84d] disabled:cursor-wait disabled:opacity-70"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? language.saving : language.sendbtn}
          </button>
        </section>
      </div>
    );
  }

  const hasValidParams = uid !== '' && urlcode !== '';

  return (
    <div id="restorepassword-page" className="mr-ui bg-[#f3f5fb] px-4 py-10 md:px-8">
      {!hasValidParams
        ? renderInvalidState()
        : msg === 'success'
          ? renderSuccessState()
          : renderFormState()}
    </div>
  );
}

export default RestorePass;
